<template>
    <div class="survey-step-wrapper">
        <SurveyStepWrapperTitle :fixed-title="fixedTitle">
            <template #default>
                <div
                    v-if="isSurveyCompleted"
                    class="mr:flex mr:items-center"
                >
                    <span
                        class="icon material-symbols-outlined mr:text-icon-big"
                        data-cy="survey-step-info-outro-header-icon"
                    >
                        {{ materialIconCheck }}
                    </span>
                    <span
                        v-if="getTitle"
                        class="mr:ml-5"
                    >
                        {{ getTitle }}
                    </span>
                </div>
                <template v-else>
                    {{ getTitle }}
                </template>
            </template>
        </SurveyStepWrapperTitle>
        <slot name="header" />

        <div
            class="survey-step-wrapper__content"
            :class="{ 'survey-step-wrapper__content--padded': fixedTitle }"
        >
            <!-- todo MSK-11786: NOTIFICATION that I need to provide header from SurveyQuestionStep -->
            <slot name="image" />

            <div class="mr:px-6 mr:md:px-8 mr:py-4 mr:md:py-6">
                <MrMarkdown
                    data-cy="survey-step-wrapper-text"
                    class="mr:survey-step-component"
                    :options="{ html: true }"
                    :source="getText"
                />

                <img
                    v-if="getImageBlobUrlFull"
                    :alt="getImageAltText"
                    class="rounded-lg mt-4 mb-n2 w-100"
                    :src="getImageBlobUrlFull"
                >

                <div
                    v-if="slots.content"
                    class="mr:pt-4 mr:md:pt-6 mr:pb-16 mr:md:pb-0"
                >
                    <slot name="content" />
                </div>

                <div
                    v-if="slots.bottomText"
                    class="mr:pt-4"
                >
                    <slot name="bottomText" />
                </div>

                <div
                    v-if="getFooterText"
                    class="mr:mt-8 mr:survey-step-component"
                    data-cy="survey-step-wrapper-footer-text"
                >
                    {{ getFooterText }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import ContentService from '@serv/ContentService'
import Locale from '@serv/Locale'
import MrMarkdown from '@comp/MrMarkdown.vue'
import SurveyStepWrapperTitle from '@comp/survey/SurveyStepWrapperTitle.vue'
import TaskUi from '@serv/TaskUi'
import { useStore } from 'vuex'
import { useSurveyStepImage } from '@composables/useSurveyStepImage'
import { computed, useSlots } from 'vue'

defineProps({
    fixedTitle: {
        type: Boolean,
        default: false
    }
})

const slots = useSlots()
const store = useStore()

// title and text. Labels used to describe the step data to the patient
const user = computed(() => store.getters.user)
const clinicianCompleteForPatient = computed(() => store.getters.clinicianCompleteForPatient)
const patient = computed(() => clinicianCompleteForPatient.value || user.value)

const getTitle = computed(() => {
    const title = ContentService.sanitiseText(TaskUi.get().getStepTitle() || '', {
        patient: patient.value,
        stripMarkdown: false
    })

    return taskHandler.value.survey.title.trim() === title.trim() ? '' : title
})

const getText = computed(() => {
    return ContentService.sanitiseText(TaskUi.get().getStepText() || '', {
        patient: patient.value,
        stripMarkdown: false
    })
})

// Optional labels. Not required to steps and provided from config
const taskHandler = computed(() => store.getters.taskHandler)

const getFooterText = computed(() => {
    return taskHandler.value.currentStep?.keyValues.footer
})

// outro step data
const isSurveyCompleted = computed(() => taskHandler.value.isOutroStep)
const materialIconCheck = 'check_circle'

// image
const { getImageBlobUrlFull } = useSurveyStepImage()

const getImageAltText = computed(() => {
    return Locale.getLanguageItem('patientSurveyStepImageAltText', [getTitle.value])
})
</script>
